/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BulkOrdersResult,
  GetAdminV1StoresStoreIdOrders200,
  GetAdminV1StoresStoreIdOrdersIdDocumentsKindPathParameters,
  GetAdminV1StoresStoreIdOrdersIdNotifications200,
  GetAdminV1StoresStoreIdOrdersIdNotificationsPathParameters,
  GetAdminV1StoresStoreIdOrdersIdPathParameters,
  GetAdminV1StoresStoreIdOrdersParams,
  GetAdminV1StoresStoreIdOrdersPathParameters,
  Order,
  OrderDocument,
  OrderNotification,
  PatchAdminV1StoresStoreIdOrdersIdBody,
  PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody,
  PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdPathParameters,
  PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody,
  PatchAdminV1StoresStoreIdOrdersIdLinesLineIdPathParameters,
  PatchAdminV1StoresStoreIdOrdersIdPathParameters,
  PostAdminV1StoresStoreIdOrdersBulkBody,
  PostAdminV1StoresStoreIdOrdersBulkPathParameters,
  PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody,
  PostAdminV1StoresStoreIdOrdersIdApprovalRecordPathParameters,
  PostAdminV1StoresStoreIdOrdersIdArchivePathParameters,
  PostAdminV1StoresStoreIdOrdersIdCancelBody,
  PostAdminV1StoresStoreIdOrdersIdCancelPathParameters,
  PostAdminV1StoresStoreIdOrdersIdDesignBody,
  PostAdminV1StoresStoreIdOrdersIdDesignPathParameters,
  PostAdminV1StoresStoreIdOrdersIdEmailsResendBody,
  PostAdminV1StoresStoreIdOrdersIdEmailsResendPathParameters,
  PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody,
  PostAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancelPathParameters,
  PostAdminV1StoresStoreIdOrdersIdFulfillmentsPathParameters,
  PostAdminV1StoresStoreIdOrdersIdHoldBody,
  PostAdminV1StoresStoreIdOrdersIdHoldPathParameters,
  PostAdminV1StoresStoreIdOrdersIdMarkPaidBody,
  PostAdminV1StoresStoreIdOrdersIdMarkPaidPathParameters,
  PostAdminV1StoresStoreIdOrdersIdNotesBody,
  PostAdminV1StoresStoreIdOrdersIdNotesPathParameters,
  PostAdminV1StoresStoreIdOrdersIdRefundsBody,
  PostAdminV1StoresStoreIdOrdersIdRefundsPathParameters,
  PostAdminV1StoresStoreIdOrdersIdReleaseBody,
  PostAdminV1StoresStoreIdOrdersIdReleasePathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Orders of the store, newest first
 */
export const getAdminV1StoresStoreIdOrders = (
    { storeId }: GetAdminV1StoresStoreIdOrdersPathParameters,
    params?: GetAdminV1StoresStoreIdOrdersParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdOrders200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdOrders200>(
      {url: `/admin/v1/stores/${storeId}/orders`, method: 'GET',
        params
    },
      options);
    }
  /**
 * add_tags and remove_tags need orders.edit, mark_paid and archive orders.write, fulfil fulfillments.write, cancel orders.cancel. Each order runs in its own transaction; the result lists per order whether it worked and the error code when it did not (an order that cannot take the action is a per-row 409, never a failed request)
 * @summary Run one action on up to 100 orders
 */
export const postAdminV1StoresStoreIdOrdersBulk = (
    { storeId }: PostAdminV1StoresStoreIdOrdersBulkPathParameters,
    postAdminV1StoresStoreIdOrdersBulkBody: BodyType<PostAdminV1StoresStoreIdOrdersBulkBody>,
 options?: SecondParameter<typeof customInstance<BulkOrdersResult>>,) => {
      return customInstance<BulkOrdersResult>(
      {url: `/admin/v1/stores/${storeId}/orders/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersBulkBody
    },
      options);
    }
  /**
 * @summary Order with lines, payments and timeline
 */
export const getAdminV1StoresStoreIdOrdersId = (
    { storeId, id }: GetAdminV1StoresStoreIdOrdersIdPathParameters,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Details only; totals never change after placement. Lines are edited through PATCH .../lines/{lineId}
 * @summary Edit details: tags, notes, email, phone, shipping address while unfulfilled
 */
export const patchAdminV1StoresStoreIdOrdersId = (
    { storeId, id }: PatchAdminV1StoresStoreIdOrdersIdPathParameters,
    patchAdminV1StoresStoreIdOrdersIdBody: BodyType<PatchAdminV1StoresStoreIdOrdersIdBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdOrdersIdBody
    },
      options);
    }
  /**
 * D-031: amount defaults to the outstanding balance; partial amounts leave the order partially_paid; the payment that settles the balance starts the production workflow (D-096). Idempotent per amount only through the balance check
 * @summary Record that a manual payment was received
 */
export const postAdminV1StoresStoreIdOrdersIdMarkPaid = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdMarkPaidPathParameters,
    postAdminV1StoresStoreIdOrdersIdMarkPaidBody: BodyType<PostAdminV1StoresStoreIdOrdersIdMarkPaidBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/mark-paid`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdMarkPaidBody
    },
      options);
    }
  /**
 * A paid order answers 409 with refundableCents unless `refund` is true, which records the refund of the whole paid balance (orders.refund) in the same transaction and then cancels; provider refunds still complete on the worker
 * @summary Cancel an unfulfilled order and release its stock
 */
export const postAdminV1StoresStoreIdOrdersIdCancel = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdCancelPathParameters,
    postAdminV1StoresStoreIdOrdersIdCancelBody: BodyType<PostAdminV1StoresStoreIdOrdersIdCancelBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/cancel`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdCancelBody
    },
      options);
    }
  /**
 * 409 for an open order or one already archived
 * @summary Archive a completed or canceled order
 */
export const postAdminV1StoresStoreIdOrdersIdArchive = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdArchivePathParameters,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/archive`, method: 'POST'
    },
      options);
    }
  /**
 * Built from the same rows as the order: store identity and logo, addresses, lines with options, customizations and stone, totals and payment state, the store's policy highlights and settings.documents.footer. The packing slip hides prices when settings.documents.showPrices is off; the invoice always shows them. The admin renders and prints it
 * @summary Packing slip or invoice as a print-ready document model
 */
export const getAdminV1StoresStoreIdOrdersIdDocumentsKind = (
    { storeId, id, kind }: GetAdminV1StoresStoreIdOrdersIdDocumentsKindPathParameters,
 options?: SecondParameter<typeof customInstance<OrderDocument>>,) => {
      return customInstance<OrderDocument>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/documents/${kind}`, method: 'GET'
    },
      options);
    }
  /**
 * Fulfillments refuse with 409 while the order is on hold. 409 when already on hold or fulfilled
 * @summary Put fulfillment on hold
 */
export const postAdminV1StoresStoreIdOrdersIdHold = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdHoldPathParameters,
    postAdminV1StoresStoreIdOrdersIdHoldBody: BodyType<PostAdminV1StoresStoreIdOrdersIdHoldBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/hold`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdHoldBody
    },
      options);
    }
  /**
 * While the design approval is pending (awaiting_design, design_sent, changes_requested) the hold stays and the call answers 409 ORDER_APPROVAL_PENDING; `force: true` with orders.edit releases anyway and writes that to the timeline
 * @summary Release a hold; the fulfillment status is recomputed from what has shipped
 */
export const postAdminV1StoresStoreIdOrdersIdRelease = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdReleasePathParameters,
    postAdminV1StoresStoreIdOrdersIdReleaseBody: BodyType<PostAdminV1StoresStoreIdOrdersIdReleaseBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/release`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdReleaseBody
    },
      options);
    }
  /**
 * @summary Add a note to the timeline
 */
export const postAdminV1StoresStoreIdOrdersIdNotes = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdNotesPathParameters,
    postAdminV1StoresStoreIdOrdersIdNotesBody: BodyType<PostAdminV1StoresStoreIdOrdersIdNotesBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/notes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdNotesBody
    },
      options);
    }
  /**
 * The answers are validated like a cart line (same options, patterns, required inputs, upload tokens). 409 once the line has shipped; 409 ORDER_LINE_PRICE_WOULD_CHANGE when the new answers carry a different price delta than what was charged
 * @summary Correct the customizations of an unshipped line
 */
export const patchAdminV1StoresStoreIdOrdersIdLinesLineId = (
    { storeId, id, lineId }: PatchAdminV1StoresStoreIdOrdersIdLinesLineIdPathParameters,
    patchAdminV1StoresStoreIdOrdersIdLinesLineIdBody: BodyType<PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/lines/${lineId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdOrdersIdLinesLineIdBody
    },
      options);
    }
  /**
 * @summary Emails sent about the order, newest first
 */
export const getAdminV1StoresStoreIdOrdersIdNotifications = (
    { storeId, id }: GetAdminV1StoresStoreIdOrdersIdNotificationsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdOrdersIdNotifications200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdOrdersIdNotifications200>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/notifications`, method: 'GET'
    },
      options);
    }
  /**
 * Queues the email through the outbox like the original and writes a send-log row at once (status queued, resentBy set); the worker fills in subject and outcome. Renders from the latest occurrence of that event on the order (tracking, RMA, amounts) and goes out even when the template is switched off or the parcel said not to notify. At most 5 resends per template per order per hour (422 LIMIT_EXCEEDED with retryAfterSeconds); the workflow emails cannot be resent here
 * @summary Send one of the order's emails again
 */
export const postAdminV1StoresStoreIdOrdersIdEmailsResend = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdEmailsResendPathParameters,
    postAdminV1StoresStoreIdOrdersIdEmailsResendBody: BodyType<PostAdminV1StoresStoreIdOrdersIdEmailsResendBody>,
 options?: SecondParameter<typeof customInstance<OrderNotification>>,) => {
      return customInstance<OrderNotification>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/emails/resend`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdEmailsResendBody
    },
      options);
    }
  /**
 * Shipping sells the reserved stock at fulfillment time (doc 05). A pending fulfillment books the lines and ships later through PATCH. Orders on hold for design approval refuse with 409
 * @summary Fulfill some or all lines
 */
export const postAdminV1StoresStoreIdOrdersIdFulfillments = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdFulfillmentsPathParameters,
    postAdminV1StoresStoreIdOrdersIdFulfillmentsBody: BodyType<PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/fulfillments`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdFulfillmentsBody
    },
      options);
    }
  /**
 * @summary Update tracking, ship a pending fulfillment or mark it delivered
 */
export const patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentId = (
    { storeId, id, fulfillmentId }: PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdPathParameters,
    patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody: BodyType<PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/fulfillments/${fulfillmentId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody
    },
      options);
    }
  /**
 * Pending fulfillments only: stock sells on ship, so nothing is restocked. 409 for shipped or delivered ones, which are reversed through a refund or a return
 * @summary Cancel a pending fulfillment so its lines are open again
 */
export const postAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancel = (
    { storeId, id, fulfillmentId }: PostAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancelPathParameters,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/fulfillments/${fulfillmentId}/cancel`, method: 'POST'
    },
      options);
    }
  /**
 * Recorded, not executed: the tenant returns the money outside Nucleus (doc 04). With restock, shipped quantities go back on hand and unshipped ones are released from the order. Card refunds answer 409 until Stripe is connected
 * @summary Record a refund on a manual payment
 */
export const postAdminV1StoresStoreIdOrdersIdRefunds = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdRefundsPathParameters,
    postAdminV1StoresStoreIdOrdersIdRefundsBody: BodyType<PostAdminV1StoresStoreIdOrdersIdRefundsBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/refunds`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdRefundsBody
    },
      options);
    }
  /**
 * Doc 05: links an uploaded media row to the order as a design_sent event, parks fulfillment on hold and emails the shopper a signed approve / request-changes link (30 days). The link is built from approvalUrl with {token}, else from checkout.customerLinkUrlTemplate or the store's primary domain (422 STORE_LINK_UNAVAILABLE without any). 409 WORKFLOW_OWNS_APPROVAL (meta.stageKey) while the order's production workflow has a review stage that approves the design
 * @summary Send a design for approval
 */
export const postAdminV1StoresStoreIdOrdersIdDesign = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdDesignPathParameters,
    postAdminV1StoresStoreIdOrdersIdDesignBody: BodyType<PostAdminV1StoresStoreIdOrdersIdDesignBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/design`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdDesignBody
    },
      options);
    }
  /**
 * approved lifts the hold (and captures a card authorization through the worker) exactly like the shopper's own answer; changes_requested keeps the hold. When the workflow's review stage is waiting for the customer, the answer is recorded on that stage. 409 when the order needs no approval or is already approved
 * @summary Record a design approval or change request given by phone or email
 */
export const postAdminV1StoresStoreIdOrdersIdApprovalRecord = (
    { storeId, id }: PostAdminV1StoresStoreIdOrdersIdApprovalRecordPathParameters,
    postAdminV1StoresStoreIdOrdersIdApprovalRecordBody: BodyType<PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody>,
 options?: SecondParameter<typeof customInstance<Order>>,) => {
      return customInstance<Order>(
      {url: `/admin/v1/stores/${storeId}/orders/${id}/approval/record`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdOrdersIdApprovalRecordBody
    },
      options);
    }
  export type GetAdminV1StoresStoreIdOrdersResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdOrders>>>
export type PostAdminV1StoresStoreIdOrdersBulkResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersBulk>>>
export type GetAdminV1StoresStoreIdOrdersIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdOrdersId>>>
export type PatchAdminV1StoresStoreIdOrdersIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdOrdersId>>>
export type PostAdminV1StoresStoreIdOrdersIdMarkPaidResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdMarkPaid>>>
export type PostAdminV1StoresStoreIdOrdersIdCancelResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdCancel>>>
export type PostAdminV1StoresStoreIdOrdersIdArchiveResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdArchive>>>
export type GetAdminV1StoresStoreIdOrdersIdDocumentsKindResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdOrdersIdDocumentsKind>>>
export type PostAdminV1StoresStoreIdOrdersIdHoldResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdHold>>>
export type PostAdminV1StoresStoreIdOrdersIdReleaseResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdRelease>>>
export type PostAdminV1StoresStoreIdOrdersIdNotesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdNotes>>>
export type PatchAdminV1StoresStoreIdOrdersIdLinesLineIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdOrdersIdLinesLineId>>>
export type GetAdminV1StoresStoreIdOrdersIdNotificationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdOrdersIdNotifications>>>
export type PostAdminV1StoresStoreIdOrdersIdEmailsResendResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdEmailsResend>>>
export type PostAdminV1StoresStoreIdOrdersIdFulfillmentsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdFulfillments>>>
export type PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentId>>>
export type PostAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancelResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancel>>>
export type PostAdminV1StoresStoreIdOrdersIdRefundsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdRefunds>>>
export type PostAdminV1StoresStoreIdOrdersIdDesignResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdDesign>>>
export type PostAdminV1StoresStoreIdOrdersIdApprovalRecordResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdOrdersIdApprovalRecord>>>
